import React from "react";
import {T} from "@tolgee/react";
import FfairPaper from "view/components/FfairPaper";
import {makeStyles} from "@material-ui/core";
import {blue, grey} from "@material-ui/core/colors";
import FlexBox from "view/components/Layout/FlexBox";
import MaintenanceModeIcon from "view/images/MaintenanceModeIcon";
import {FFairPrimaryIconGreyLogo} from "view/images/FFairLogo";
import PublicLanguagePicker from "view/components/LanguagePicker/PublicLanguagePicker";
import {useMaintenanceModeStore} from "store/general/maintenanceMode.store";
import {useLocalisedLanguageCode} from "hooks/translation";
import {sanitizeHtml} from "view/components/SafeHtml";

function MaintenanceMode() {
  const {maintenanceMode} = useMaintenanceModeStore();
  const classes = useStyles();

  const languageCode = useLocalisedLanguageCode(true);
  const description =
    maintenanceMode?.descriptionTranslations?.[languageCode] ||
    maintenanceMode?.description ||
    "";
  return (
    <FfairPaper className={classes.root}>
      <FlexBox column className={classes.messageContainer}>
        <FlexBox column gap={2} alignCenter>
          <FFairPrimaryIconGreyLogo width={64} height={64} />
          <span className={classes.welcomeText}>
            <T keyName="global.maintenance.welcome" />
          </span>
        </FlexBox>
        <MaintenanceModeIcon />
        <span className={classes.improvementText}>
          <T keyName="global.maintenance.message" />
        </span>
        <div
          data-testid="maintenance-description"
          className={classes.description}
          dangerouslySetInnerHTML={{__html: sanitizeHtml(description)}}
        />
      </FlexBox>
      <FlexBox column gap={2} alignCenter>
        <FlexBox gap={1} alignCenter>
          <FFairPrimaryIconGreyLogo width={24} height={24} />
          <span className={classes.footer}>
            Copyright © FFAIR Ltd {new Date().getFullYear()}.
          </span>
        </FlexBox>
        <PublicLanguagePicker />
      </FlexBox>
    </FfairPaper>
  );
}

export default MaintenanceMode;

const useStyles = makeStyles(({spacing}) => ({
  // Also rendered inside the embedded show list iframe, where the viewport can
  // be shorter than the card, so the footer flows after it instead of being
  // pinned to the viewport bottom.
  root: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    boxSizing: "border-box",
    width: "100%",
    minHeight: "100vh",
    padding: spacing(3, 2, 5, 2),
    gap: spacing(3),
  },
  messageContainer: {
    padding: spacing(5.75, 3, 4, 3),
    borderRadius: "12px",
    border: `1px solid ${grey[300]}`,
    gap: spacing(3),
    boxSizing: "border-box",
    width: "100%",
    maxWidth: "440px",
    margin: "auto 0",
    alignItems: "center",
  },
  description: {
    textAlign: "center",
    maxWidth: "100%",
    minWidth: 0,
    overflowWrap: "anywhere",
    wordBreak: "break-word",
    "& ul, & ol": {
      display: "inline-block",
      maxWidth: "100%",
      boxSizing: "border-box",
      margin: 0,
      paddingLeft: spacing(3),
      textAlign: "left",
    },
    "& img, & table, & pre": {maxWidth: "100%"},
    "& pre": {whiteSpace: "pre-wrap"},
  },
  welcomeText: {
    fontWeight: 500,
    fontSize: "24px",
  },
  footer: {
    color: grey[500],
    fontSize: "14px",
  },
  improvementText: {
    color: blue.A400,
    fontSize: "16px",
    fontWeight: 500,
    letterSpacing: "0.15px",
    textAlign: "center",
  },
}));
